import { Timestamp } from 'firebase/firestore';

/**
 * Formats a Date to YYYY-MM-DD using LOCAL time.
 * Never use toISOString() for date strings - it returns UTC and causes
 * a one-day offset for timezones ahead of UTC (e.g. Germany CEST = UTC+2).
 */
const toLocalDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

/**
 * ScheduleShift Model (ENHANCED)
 * Represents an individual work shift for a staff member.
 * Supports both the weekly schedule embedded format and the individual
 * shift subcollection format (schedules/{staffId}/shifts/{shiftId}).
 */
export class ScheduleShift {
    constructor({
        id = null,
        shiftId = null,
        staffId,
        departmentId,
        date,
        dayOfWeek = null,
        startTime,
        endTime,
        expectedHours = 0,
        durationMinutes = 0,
        isTemplate = false,
        notes = '',
        createdBy = '',
        createdAt = null,
        updatedAt = null,
        batchId = null,
        recurrence = null,
        repeatEnd = null
    }) {
        this.id = id;
        this.shiftId = shiftId || this.generateShiftId();
        this.staffId = staffId;
        this.departmentId = departmentId;
        this.date = date;

        this.dayOfWeek = dayOfWeek || (date ? ScheduleUtils.getDayOfWeek(date) : null);

        this.startTime = this.normalizeTime(startTime);
        this.endTime = this.normalizeTime(endTime);

        this.startTimeString = this.timeToString(this.startTime);
        this.endTimeString = this.timeToString(this.endTime);

        this.expectedHours = expectedHours;
        this.durationMinutes = durationMinutes;
        this.isTemplate = isTemplate;
        this.notes = notes;
        this.createdBy = createdBy;
        this.createdAt = createdAt || Timestamp.now();
        this.updatedAt = updatedAt || Timestamp.now();

        this.batchId = batchId;
        this.recurrence = recurrence;
        this.repeatEnd = repeatEnd;

        if (this.durationMinutes === 0 && this.startTime && this.endTime) {
            this.durationMinutes = this.calculateDurationMinutes();
        }

        if (this.expectedHours === 0 && this.durationMinutes > 0) {
            this.expectedHours = this.durationMinutes / 60;
        }
    }

    /**
     * Generate unique shift ID.
     */
    generateShiftId() {
        return `shift_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Normalize time input to Timestamp for internal calculations.
     * startTimeString / endTimeString hold the plain "HH:MM" form used
     * when writing to Firestore via toFirestore().
     */
    normalizeTime(time) {
        if (!time) return null;

        if (time instanceof Timestamp) return time;

        if (time instanceof Date) return Timestamp.fromDate(time);

        if (typeof time === 'string') {
            return ScheduleUtils.parseTimeToTimestamp(time, this.date);
        }

        return null;
    }

    /**
     * Convert time to string format (HH:MM).
     */
    timeToString(time) {
        if (!time) return '';

        if (typeof time === 'string') return time;

        if (time instanceof Timestamp) {
            return ScheduleUtils.formatTime(time);
        }

        return '';
    }

    /**
     * Calculate duration in minutes from internal Timestamp values.
     */
    calculateDurationMinutes() {
        if (!this.startTime || !this.endTime) return 0;

        const startDate = this.startTime.toDate();
        const endDate = this.endTime.toDate();
        const diffInMs = endDate.getTime() - startDate.getTime();

        return Math.max(0, Math.round(diffInMs / (1000 * 60)));
    }

    /**
     * Calculate expected work hours for this shift.
     */
    calculateExpectedHours() {
        return this.durationMinutes / 60;
    }

    /**
     * Get shift duration in hours and minutes format.
     */
    getFormattedDuration() {
        const hours = Math.floor(this.durationMinutes / 60);
        const minutes = this.durationMinutes % 60;

        if (hours === 0) return `${minutes}min`;
        if (minutes === 0) return `${hours}h`;
        return `${hours}h ${minutes}min`;
    }

    /**
     * Check if shift overlaps with another shift.
     */
    overlapsWith(otherShift) {
        if (this.date !== otherShift.date) return false;

        const thisStart = this.startTime.toDate();
        const thisEnd = this.endTime.toDate();
        const otherStart = otherShift.startTime.toDate();
        const otherEnd = otherShift.endTime.toDate();

        return thisStart < otherEnd && thisEnd > otherStart;
    }

    /**
     * Check if a specific time slot (in minutes from midnight) falls within this shift.
     */
    containsTimeSlot(timeSlotMinutes) {
        const startMinutes = ScheduleUtils.timestampToMinutes(this.startTime);
        const endMinutes = ScheduleUtils.timestampToMinutes(this.endTime);
        return timeSlotMinutes >= startMinutes && timeSlotMinutes < endMinutes;
    }

    /**
     * Validate shift data.
     */
    validate() {
        const errors = [];

        if (!this.staffId)      errors.push('Staff ID is required');
        if (!this.departmentId) errors.push('Department ID is required');
        if (!this.date)         errors.push('Date is required');
        if (!this.startTime)    errors.push('Start time is required');
        if (!this.endTime)      errors.push('End time is required');

        if (this.startTime && this.endTime) {
            const startDate = this.startTime.toDate();
            const endDate = this.endTime.toDate();

            if (startDate >= endDate) {
                errors.push('End time must be after start time');
            }

            if (this.durationMinutes > 24 * 60) {
                errors.push('Shift cannot be longer than 24 hours');
            }

            if (this.durationMinutes < 15) {
                errors.push('Shift must be at least 15 minutes long');
            }
        }

        return errors;
    }

    /**
     * Convert to Firestore document format.
     *
     * startTime and endTime are written as plain "HH:MM" strings using
     * startTimeString / endTimeString. Never write the internal Timestamp
     * values (this.startTime / this.endTime) to Firestore - doing so produces
     * documents that the iOS fromFirestore() parser cannot read without the
     * Timestamp fallback path.
     *
     * ADDED: staffId and createdBy are now included to align with the Firebase
     * schema and remove the need for the iOS path-extraction fallback on every
     * document created from the web app.
     *
     * totalMinutes is written alongside durationMinutes so that ShiftModel
     * and the iOS parser both find the field they expect.
     */
    toFirestore() {
        return {
            staffId:         this.staffId,         // ADDED: was absent, required by Firebase schema
            shiftId:         this.shiftId,
            dayOfWeek:       this.dayOfWeek,
            date:            this.date,
            startTime:       this.startTimeString,  // string "HH:MM" - never write this.startTime (Timestamp)
            endTime:         this.endTimeString,    // string "HH:MM" - never write this.endTime (Timestamp)
            durationMinutes: this.durationMinutes,
            totalMinutes:    this.durationMinutes,  // parallel field for ShiftModel and iOS compatibility
            departmentId:    this.departmentId,
            notes:           this.notes,
            createdBy:       this.createdBy,        // ADDED: was absent, required by Firebase schema
            createdAt:       this.createdAt,
            updatedAt:       Timestamp.now()
        };
    }

    /**
     * @deprecated
     *
     * THIS METHOD MUST NOT BE CALLED.
     *
     * It wrote this.startTime and this.endTime as Firestore Timestamp objects.
     * The iOS ScheduleShift.fromFirestore() parser expected plain "HH:MM" strings
     * and returned nil for every document produced by this method, causing all
     * web-created shifts to be silently discarded on iOS.
     *
     * The Timestamp fallback added to iOS fromFirestore() (Priority 1 fix) handles
     * documents already in Firestore in this format. No new documents should ever
     * be written this way.
     *
     * Use toFirestore() instead.
     */
    toLegacyFirestore() {
        throw new Error(
            'ScheduleShift.toLegacyFirestore() is disabled. ' +
            'This method wrote startTime and endTime as Firestore Timestamp objects, ' +
            'which broke iOS schedule parsing and caused web-created shifts to not appear on iOS. ' +
            'Use toFirestore() instead.'
        );
    }

    /**
     * Create from Firestore document.
     */
    static fromFirestore(doc) {
        if (!doc.exists()) return null;

        const data = doc.data();
        return new ScheduleShift({
            id: doc.id,
            ...data
        });
    }

    /**
     * Create from weekly schedule shift data.
     */
    static fromWeeklyScheduleShift(shiftData, staffId) {
        return new ScheduleShift({
            shiftId:         shiftData.shiftId,
            staffId:         staffId,
            departmentId:    shiftData.departmentId,
            date:            shiftData.date,
            dayOfWeek:       shiftData.dayOfWeek,
            startTime:       shiftData.startTime,
            endTime:         shiftData.endTime,
            durationMinutes: shiftData.durationMinutes,
            notes:           shiftData.notes || '',
            createdAt:       shiftData.createdAt,
            updatedAt:       shiftData.updatedAt
        });
    }

    /**
     * Create a copy of this shift for a different date.
     */
    copyForDate(newDate) {
        return new ScheduleShift({
            staffId:         this.staffId,
            departmentId:    this.departmentId,
            date:            newDate,
            dayOfWeek:       ScheduleUtils.getDayOfWeek(newDate),
            startTime:       this.startTimeString,
            endTime:         this.endTimeString,
            durationMinutes: this.durationMinutes,
            expectedHours:   this.expectedHours,
            isTemplate:      this.isTemplate,
            notes:           this.notes,
            createdBy:       this.createdBy
        });
    }
}

/**
 * WeeklySchedule Model
 * Represents a week's worth of shifts for a staff member.
 * Primary model for the embedded weekly schedule Firestore format.
 */
export class WeeklySchedule {
    constructor({
        id = null,
        staffId,
        departmentId,
        weekStartDate,
        weekNumber = null,
        year = null,
        shifts = [],
        totalWeekMinutes = 0,
        totalWeekHours = 0,
        isPublished = false,
        publishedAt = null,
        createdBy = '',
        createdAt = null,
        updatedAt = null,
        version = 1
    }) {
        this.id = id;
        this.staffId = staffId;
        this.departmentId = departmentId;
        this.weekStartDate = weekStartDate;

        if (weekNumber === null || year === null) {
            const weekInfo = ScheduleUtils.getWeekNumber(weekStartDate);
            this.weekNumber = weekInfo.week;
            this.year = weekInfo.year;
        } else {
            this.weekNumber = weekNumber;
            this.year = year;
        }

        this.shifts = shifts.map(shift =>
            shift instanceof ScheduleShift
                ? shift
                : ScheduleShift.fromWeeklyScheduleShift(shift, staffId)
        );

        this.totalWeekMinutes = totalWeekMinutes;
        this.totalWeekHours = totalWeekHours;
        this.isPublished = isPublished;
        this.publishedAt = publishedAt;
        this.createdBy = createdBy;
        this.createdAt = createdAt || Timestamp.now();
        this.updatedAt = updatedAt || Timestamp.now();
        this.version = version;

        if (this.totalWeekMinutes === 0 && this.shifts.length > 0) {
            this.calculateTotals();
        }
    }

    /**
     * Calculate total week minutes and hours.
     */
    calculateTotals() {
        this.totalWeekMinutes = this.shifts.reduce(
            (total, shift) => total + shift.durationMinutes,
            0
        );
        this.totalWeekHours = parseFloat((this.totalWeekMinutes / 60).toFixed(2));
    }

    /**
     * Add shift to week.
     */
    addShift(shift) {
        if (!(shift instanceof ScheduleShift)) {
            shift = new ScheduleShift({ ...shift, staffId: this.staffId });
        }

        const shiftDate = new Date(shift.date);
        const weekStart = new Date(this.weekStartDate);
        const weekEnd   = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        if (shiftDate < weekStart || shiftDate > weekEnd) {
            throw new Error('Shift date is outside this week');
        }

        this.shifts.push(shift);
        this.calculateTotals();
        this.updatedAt = Timestamp.now();
    }

    /**
     * Remove shift by shiftId.
     */
    removeShift(shiftId) {
        this.shifts = this.shifts.filter(shift => shift.shiftId !== shiftId);
        this.calculateTotals();
        this.updatedAt = Timestamp.now();
    }

    /**
     * Update existing shift.
     */
    updateShift(shiftId, updates) {
        const shiftIndex = this.shifts.findIndex(shift => shift.shiftId === shiftId);

        if (shiftIndex !== -1) {
            this.shifts[shiftIndex] = new ScheduleShift({
                ...this.shifts[shiftIndex],
                ...updates,
                shiftId:   shiftId,
                staffId:   this.staffId,
                updatedAt: Timestamp.now()
            });
            this.calculateTotals();
            this.updatedAt = Timestamp.now();
        }
    }

    /**
     * Get shifts for specific day.
     */
    getShiftsForDay(dayOfWeek) {
        return this.shifts.filter(shift => shift.dayOfWeek === dayOfWeek);
    }

    /**
     * Get shifts for specific date.
     */
    getShiftsForDate(date) {
        return this.shifts.filter(shift => shift.date === date);
    }

    /**
     * Check for overlapping shifts.
     */
    hasOverlaps() {
        for (let i = 0; i < this.shifts.length; i++) {
            for (let j = i + 1; j < this.shifts.length; j++) {
                if (this.shifts[i].overlapsWith(this.shifts[j])) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Get all overlapping shift pairs.
     */
    getOverlaps() {
        const overlaps = [];

        for (let i = 0; i < this.shifts.length; i++) {
            for (let j = i + 1; j < this.shifts.length; j++) {
                if (this.shifts[i].overlapsWith(this.shifts[j])) {
                    overlaps.push({
                        shift1: this.shifts[i],
                        shift2: this.shifts[j]
                    });
                }
            }
        }

        return overlaps;
    }

    /**
     * Validate weekly schedule.
     */
    validate() {
        const errors = [];

        if (!this.staffId)      errors.push('Staff ID is required');
        if (!this.weekStartDate) errors.push('Week start date is required');

        this.shifts.forEach((shift, index) => {
            const shiftErrors = shift.validate();
            if (shiftErrors.length > 0) {
                errors.push(`Shift ${index + 1}: ${shiftErrors.join(', ')}`);
            }
        });

        if (this.hasOverlaps()) {
            errors.push('Schedule contains overlapping shifts');
        }

        return errors;
    }

    /**
     * Publish schedule.
     */
    publish() {
        this.isPublished = true;
        this.publishedAt = Timestamp.now();
        this.updatedAt   = Timestamp.now();
    }

    /**
     * Unpublish schedule.
     */
    unpublish() {
        this.isPublished = false;
        this.publishedAt = null;
        this.updatedAt   = Timestamp.now();
    }

    /**
     * Convert to Firestore document format.
     * Each embedded shift is serialised via ScheduleShift.toFirestore(),
     * which now includes staffId and createdBy.
     */
    toFirestore() {
        return {
            staffId:          this.staffId,
            departmentId:     this.departmentId,
            weekStartDate:    this.weekStartDate,
            weekNumber:       this.weekNumber,
            year:             this.year,
            shifts:           this.shifts.map(shift => shift.toFirestore()),
            totalWeekMinutes: this.totalWeekMinutes,
            totalWeekHours:   this.totalWeekHours,
            isPublished:      this.isPublished,
            publishedAt:      this.publishedAt,
            createdBy:        this.createdBy,
            createdAt:        this.createdAt,
            updatedAt:        Timestamp.now(),
            version:          this.version
        };
    }

    /**
     * Create from Firestore document.
     */
    static fromFirestore(doc) {
        if (!doc.exists()) return null;

        const data = doc.data();
        return new WeeklySchedule({
            id: doc.id,
            ...data
        });
    }

    /**
     * Create empty weekly schedule for a staff member.
     */
    static createEmpty(staffId, departmentId, weekStartDate, createdBy) {
        return new WeeklySchedule({
            staffId,
            departmentId,
            weekStartDate,
            shifts: [],
            createdBy
        });
    }

    /**
     * Get week date range.
     * Uses toLocalDateString() instead of toISOString() to prevent a one-day
     * offset for timezones ahead of UTC (e.g. Germany CEST = UTC+2).
     */
    getWeekDateRange() {
        const startDate = new Date(this.weekStartDate);
        const endDate   = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);

        return {
            start: this.weekStartDate,
            end:   toLocalDateString(endDate)
        };
    }

    /**
     * Get formatted week label (e.g., "Week 42, 2025").
     */
    getWeekLabel() {
        return `Week ${this.weekNumber}, ${this.year}`;
    }

    /**
     * Get formatted date range label (e.g., "Oct 20 - Oct 26, 2025").
     */
    getDateRangeLabel() {
        const range     = this.getWeekDateRange();
        const startDate = new Date(range.start);
        const endDate   = new Date(range.end);

        const options  = { month: 'short', day: 'numeric' };
        const startStr = startDate.toLocaleDateString('en-US', options);
        const endStr   = endDate.toLocaleDateString('en-US', options);

        return `${startStr} - ${endStr}, ${this.year}`;
    }
}

/**
 * ShiftModel
 * Maps directly to an individual document in the
 * schedules/{staffId}/shifts/{shiftId} subcollection.
 * This is the model read by the iOS app via ScheduleShift.fromFirestore().
 */
export class ShiftModel {
    constructor({
        id = null,
        staffId = null,         // ADDED: required by Firebase schema; prevents iOS path-extraction fallback
        date,
        startTime,
        endTime,
        totalMinutes,
        departmentId,
        createdAt = new Date(),
        createdBy = null,
        updatedAt = null,
        updatedBy = null,
        batchId = null,
        recurrence = null,
        repeatEnd = null
    }) {
        this.id           = id;
        this.staffId      = staffId;    // ADDED
        this.date         = date;
        this.startTime    = startTime;
        this.endTime      = endTime;
        this.totalMinutes = totalMinutes;
        this.departmentId = departmentId;
        this.createdAt    = createdAt;
        this.createdBy    = createdBy;
        this.updatedAt    = updatedAt;
        this.updatedBy    = updatedBy;
        this.batchId      = batchId;
        this.recurrence   = recurrence;
        this.repeatEnd    = repeatEnd;
    }

    /**
     * Convert to Firestore document format.
     * ADDED: staffId is now included so iOS does not need the path-extraction
     * fallback for documents written by ShiftModel.
     */
    toFirestore() {
        const data = {
            staffId:      this.staffId,   // ADDED: was absent, required by Firebase schema
            date:         this.date,
            startTime:    this.startTime,
            endTime:      this.endTime,
            totalMinutes: this.totalMinutes,
            departmentId: this.departmentId,
            createdAt:    this.createdAt,
            createdBy:    this.createdBy
        };

        if (this.updatedAt)  data.updatedAt  = this.updatedAt;
        if (this.updatedBy)  data.updatedBy  = this.updatedBy;
        if (this.batchId)    data.batchId    = this.batchId;
        if (this.recurrence) data.recurrence = this.recurrence;
        if (this.repeatEnd)  data.repeatEnd  = this.repeatEnd;

        return data;
    }

    /**
     * Create from Firestore document.
     * totalMinutes falls back to durationMinutes for documents written by
     * ScheduleShift.toFirestore() which uses durationMinutes as the field name.
     */
    static fromFirestore(doc) {
        const data = doc.data();
        return new ShiftModel({
            id:           doc.id,
            ...data,
            totalMinutes: data.totalMinutes ?? data.durationMinutes ?? 0,
            createdAt:    data.createdAt?.toDate(),
            updatedAt:    data.updatedAt?.toDate(),
            batchId:      data.batchId    || null,
            recurrence:   data.recurrence || null,
            repeatEnd:    data.repeatEnd  || null
        });
    }
}

/**
 * ScheduleTemplate Model
 * Represents a reusable schedule pattern for bulk operations.
 */
export class ScheduleTemplate {
    constructor({
        id = null,
        name,
        departmentId,
        shifts = [],
        description = '',
        createdBy = '',
        createdAt = null
    }) {
        this.id           = id;
        this.name         = name;
        this.departmentId = departmentId;
        this.shifts       = shifts.map(shift =>
            shift instanceof ScheduleShift ? shift : new ScheduleShift(shift)
        );
        this.description  = description;
        this.createdBy    = createdBy;
        this.createdAt    = createdAt || Timestamp.now();
    }

    /**
     * Apply template to specific dates for a staff member.
     */
    applyToStaff(staffId, dates) {
        const generatedShifts = [];

        dates.forEach(date => {
            this.shifts.forEach(templateShift => {
                const shift      = templateShift.copyForDate(date);
                shift.staffId    = staffId;
                shift.isTemplate = false;
                generatedShifts.push(shift);
            });
        });

        return generatedShifts;
    }

    /**
     * Convert to Firestore document format.
     */
    toFirestore() {
        return {
            name:         this.name,
            departmentId: this.departmentId,
            shifts:       this.shifts.map(shift => shift.toFirestore()),
            description:  this.description,
            createdBy:    this.createdBy,
            createdAt:    this.createdAt
        };
    }

    /**
     * Create from Firestore document.
     */
    static fromFirestore(doc) {
        if (!doc.exists()) return null;

        const data = doc.data();
        return new ScheduleTemplate({
            id: doc.id,
            ...data,
            shifts: data.shifts?.map(shift => new ScheduleShift(shift)) || []
        });
    }
}

/**
 * SchedulePeriod Model
 * Represents a collection of shifts for a specific time period.
 */
export class SchedulePeriod {
    constructor({
        staffId,
        startDate,
        endDate,
        shifts = [],
        holidays = []
    }) {
        this.staffId   = staffId;
        this.startDate = startDate;
        this.endDate   = endDate;
        this.shifts    = shifts.map(shift =>
            shift instanceof ScheduleShift ? shift : new ScheduleShift(shift)
        );
        this.holidays  = holidays;
    }

    /**
     * Calculate total scheduled hours for the period.
     */
    getTotalScheduledHours() {
        return this.shifts.reduce((total, shift) => total + shift.expectedHours, 0);
    }

    /**
     * Calculate total hours adjusted for holidays.
     */
    getAdjustedHours() {
        let adjustedHours = this.getTotalScheduledHours();

        this.holidays.forEach(holiday => {
            if (!holiday.isPaidHoliday) {
                const holidayShifts = this.shifts.filter(
                    shift => shift.date === holiday.dateString
                );
                const holidayHours = holidayShifts.reduce(
                    (total, shift) => total + shift.expectedHours, 0
                );
                adjustedHours -= holidayHours;
            }
        });

        return Math.max(0, adjustedHours);
    }

    /**
     * Get shifts by date.
     */
    getShiftsForDate(date) {
        return this.shifts.filter(shift => shift.date === date);
    }

    /**
     * Get all unique dates with shifts.
     */
    getScheduledDates() {
        return [...new Set(this.shifts.map(shift => shift.date))].sort();
    }

    /**
     * Check for shift conflicts (overlapping shifts).
     */
    getShiftConflicts() {
        const conflicts = [];

        for (let i = 0; i < this.shifts.length; i++) {
            for (let j = i + 1; j < this.shifts.length; j++) {
                if (this.shifts[i].overlapsWith(this.shifts[j])) {
                    conflicts.push({
                        shift1: this.shifts[i],
                        shift2: this.shifts[j],
                        date:   this.shifts[i].date
                    });
                }
            }
        }

        return conflicts;
    }

    /**
     * Get weekly summary (Monday to Sunday).
     */
    getWeeklySummary() {
        const weeks = {};

        this.shifts.forEach(shift => {
            const weekStart = ScheduleUtils.getWeekStartDate(shift.date);

            if (!weeks[weekStart]) {
                weeks[weekStart] = {
                    weekStart,
                    shifts:     [],
                    totalHours: 0
                };
            }

            weeks[weekStart].shifts.push(shift);
            weeks[weekStart].totalHours += shift.expectedHours;
        });

        return Object.values(weeks);
    }

    /**
     * Add shift to period.
     */
    addShift(shift) {
        if (!(shift instanceof ScheduleShift)) {
            shift = new ScheduleShift(shift);
        }

        if (shift.date < this.startDate || shift.date > this.endDate) {
            throw new Error('Shift date is outside the schedule period');
        }

        this.shifts.push(shift);
    }

    /**
     * Remove shift from period.
     */
    removeShift(shiftId) {
        this.shifts = this.shifts.filter(shift => shift.shiftId !== shiftId);
    }

    /**
     * Update existing shift.
     */
    updateShift(shiftId, updates) {
        const shiftIndex = this.shifts.findIndex(shift => shift.shiftId === shiftId);
        if (shiftIndex !== -1) {
            this.shifts[shiftIndex] = new ScheduleShift({
                ...this.shifts[shiftIndex],
                ...updates,
                shiftId:   shiftId,
                updatedAt: Timestamp.now()
            });
        }
    }
}

/**
 * TimeSlot Model
 * Represents a 15-minute time slot for schedule planning.
 */
export class TimeSlot {
    constructor(timeString) {
        this.time = timeString;
        const [hours, minutes] = timeString.split(':').map(Number);
        this.hours        = hours;
        this.minutes      = minutes;
        this.totalMinutes = hours * 60 + minutes;
    }

    /**
     * Get display time for this slot.
     */
    getDisplayTime() {
        return this.time;
    }

    /**
     * Check if this slot is on an hour boundary.
     */
    isHourBoundary() {
        return this.minutes === 0;
    }

    /**
     * Get next time slot (15 minutes later).
     */
    getNext() {
        const nextMinutes      = this.totalMinutes + 15;
        const nextHours        = Math.floor(nextMinutes / 60);
        const remainingMinutes = nextMinutes % 60;

        if (nextHours >= 24) return null;

        return new TimeSlot(
            `${nextHours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}`
        );
    }

    /**
     * Get previous time slot (15 minutes earlier).
     */
    getPrevious() {
        const prevMinutes = this.totalMinutes - 15;
        if (prevMinutes < 0) return null;

        const prevHours        = Math.floor(prevMinutes / 60);
        const remainingMinutes = prevMinutes % 60;

        return new TimeSlot(
            `${prevHours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}`
        );
    }

    /**
     * Check if this slot is within business hours (6 AM to 10 PM).
     */
    isBusinessHours() {
        return this.hours >= 6 && this.hours <= 22;
    }
}

/**
 * Utility functions for schedule operations (ENHANCED).
 */
export class ScheduleUtils {
    /**
     * Get week start date (Monday) for any given date.
     * Uses toLocalDateString() instead of toISOString() for timezone safety.
     */
    static getWeekStartDate(date) {
        const dateObj = new Date(date);
        const day     = dateObj.getDay();
        const diff    = dateObj.getDate() - day + (day === 0 ? -6 : 1);
        const monday  = new Date(dateObj.setDate(diff));
        return toLocalDateString(monday);
    }

    /**
     * Get ISO week number and year for a date.
     */
    static getWeekNumber(date) {
        const dateObj  = new Date(date);
        const target   = new Date(dateObj.valueOf());
        const dayNr    = (dateObj.getDay() + 6) % 7;
        target.setDate(target.getDate() - dayNr + 3);
        const firstThursday = target.valueOf();
        target.setMonth(0, 1);
        if (target.getDay() !== 4) {
            target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
        }
        const weekNumber = 1 + Math.ceil((firstThursday - target) / 604800000);

        return {
            week: weekNumber,
            year: target.getFullYear()
        };
    }

    /**
     * Get day of week name from date.
     */
    static getDayOfWeek(dateString) {
        const date = new Date(dateString);
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        return days[date.getDay()];
    }

    /**
     * Generate all dates in a week from Monday date.
     * Uses toLocalDateString() instead of toISOString() for timezone safety.
     */
    static getWeekDates(mondayDate) {
        const dates     = [];
        const startDate = new Date(mondayDate);

        for (let i = 0; i < 7; i++) {
            const date    = new Date(startDate);
            date.setDate(startDate.getDate() + i);
            const dateStr = toLocalDateString(date);
            dates.push({
                date:      dateStr,
                dayOfWeek: this.getDayOfWeek(dateStr),
                dayName:   date.toLocaleDateString('en-US', { weekday: 'long' })
            });
        }

        return dates;
    }

    /**
     * Generate date range between start and end dates.
     * Uses toLocalDateString() instead of toISOString() for timezone safety.
     */
    static generateDateRange(startDate, endDate) {
        const dates       = [];
        const currentDate = new Date(startDate);
        const endDateObj  = new Date(endDate);

        while (currentDate <= endDateObj) {
            dates.push(toLocalDateString(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return dates;
    }

    /**
     * Get week boundaries for a given date.
     * Uses toLocalDateString() instead of toISOString() for timezone safety.
     */
    static getWeekBoundaries(date) {
        const weekStart = this.getWeekStartDate(date);
        const startDate = new Date(weekStart);
        const endDate   = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);

        return {
            start: weekStart,
            end:   toLocalDateString(endDate)
        };
    }

    /**
     * Get month boundaries for a given date.
     *
     * Builds the YYYY-MM-DD strings directly from numeric parts, eliminating
     * any UTC conversion. The previous toISOString() approach shifted the end
     * date one day backwards for timezones ahead of UTC (e.g. Germany CEST
     * = UTC+2), causing the last day of the month to be excluded from every
     * Firestore query.
     */
    static getMonthBoundaries(date) {
        const dateObj = new Date(date);
        const year    = dateObj.getFullYear();
        const month   = dateObj.getMonth(); // 0-indexed

        const lastDay = new Date(year, month + 1, 0).getDate();

        return {
            start: `${year}-${String(month + 1).padStart(2, '0')}-01`,
            end:   `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
        };
    }

    /**
     * Check if date is a holiday.
     */
    static isHoliday(date, holidays) {
        return holidays.some(holiday => holiday.dateString === date);
    }

    /**
     * Format time from Timestamp for display.
     */
    static formatTime(timestamp) {
        if (!timestamp) return '';

        const date    = timestamp.toDate();
        const hours   = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        return `${hours}:${minutes}`;
    }

    /**
     * Parse time string to create Timestamp for specific date.
     */
    static parseTimeToTimestamp(timeString, date = null) {
        const [hours, minutes] = timeString.split(':').map(Number);
        const targetDate       = date ? new Date(date) : new Date();
        targetDate.setHours(hours, minutes, 0, 0);
        return Timestamp.fromDate(targetDate);
    }

    /**
     * Convert time string (HH:MM) to minutes from midnight.
     */
    static parseTimeToMinutes(timeString) {
        if (!timeString || typeof timeString !== 'string') return 0;

        const [hours, minutes] = timeString.split(':').map(Number);
        if (isNaN(hours) || isNaN(minutes)) return 0;

        return hours * 60 + minutes;
    }

    /**
     * Convert Firebase Timestamp to minutes from midnight.
     */
    static timestampToMinutes(timestamp) {
        if (!timestamp) return 0;

        const date = timestamp.toDate();
        return date.getHours() * 60 + date.getMinutes();
    }

    /**
     * Add minutes to a time string and return new time string.
     */
    static addMinutesToTime(timeString, minutesToAdd) {
        if (!timeString || typeof timeString !== 'string') return '00:00';

        const totalMinutes = this.parseTimeToMinutes(timeString) + minutesToAdd;
        const hours        = Math.floor(totalMinutes / 60) % 24;
        const minutes      = totalMinutes % 60;

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    /**
     * Convert minutes from midnight to time string (HH:MM).
     */
    static minutesToTimeString(minutes) {
        const hours = Math.floor(minutes / 60) % 24;
        const mins  = minutes % 60;
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    }

    /**
     * Generate array of 15-minute time slots for a day.
     */
    static generateTimeSlots(startHour = 6, endHour = 23) {
        const slots = [];

        for (let hour = startHour; hour <= endHour; hour++) {
            for (let minute = 0; minute < 60; minute += 15) {
                const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                slots.push(new TimeSlot(timeString));
            }
        }

        return slots;
    }

    /**
     * Check if two time ranges overlap.
     */
    static timeRangesOverlap(start1, end1, start2, end2) {
        const start1Minutes = this.parseTimeToMinutes(start1);
        const end1Minutes   = this.parseTimeToMinutes(end1);
        const start2Minutes = this.parseTimeToMinutes(start2);
        const end2Minutes   = this.parseTimeToMinutes(end2);

        return start1Minutes < end2Minutes && end1Minutes > start2Minutes;
    }

    /**
     * Calculate duration in hours between two time strings.
     */
    static calculateDurationHours(startTime, endTime) {
        const startMinutes = this.parseTimeToMinutes(startTime);
        const endMinutes   = this.parseTimeToMinutes(endTime);
        let duration       = endMinutes - startMinutes;

        if (duration < 0) {
            duration += 24 * 60;
        }

        return duration / 60;
    }

    /**
     * Calculate duration in minutes between two time strings.
     */
    static calculateDurationMinutes(startTime, endTime) {
        const startMinutes = this.parseTimeToMinutes(startTime);
        const endMinutes   = this.parseTimeToMinutes(endTime);
        let duration       = endMinutes - startMinutes;

        if (duration < 0) {
            duration += 24 * 60;
        }

        return duration;
    }

    /**
     * Round time to nearest 15-minute interval.
     */
    static roundToNearestQuarter(timeString) {
        const minutes        = this.parseTimeToMinutes(timeString);
        const roundedMinutes = Math.round(minutes / 15) * 15;
        return this.minutesToTimeString(roundedMinutes);
    }

    /**
     * Get time slots between start and end time.
     */
    static getTimeSlotsInRange(startTime, endTime) {
        const slots        = [];
        const startMinutes = this.parseTimeToMinutes(startTime);
        const endMinutes   = this.parseTimeToMinutes(endTime);

        if (endMinutes >= startMinutes) {
            for (let minutes = startMinutes; minutes < endMinutes; minutes += 15) {
                slots.push(this.minutesToTimeString(minutes));
            }
        } else {
            for (let minutes = startMinutes; minutes < 24 * 60; minutes += 15) {
                slots.push(this.minutesToTimeString(minutes));
            }
            for (let minutes = 0; minutes < endMinutes; minutes += 15) {
                slots.push(this.minutesToTimeString(minutes));
            }
        }

        return slots;
    }

    /**
     * Format duration in minutes to readable format.
     */
    static formatDuration(totalMinutes) {
        const hours   = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        if (hours === 0) {
            return `${minutes}min`;
        } else if (minutes === 0) {
            return `${hours}h`;
        } else {
            return `${hours}h ${minutes}min`;
        }
    }

    /**
     * Check if time string is valid (HH:MM format).
     */
    static isValidTimeString(timeString) {
        if (!timeString || typeof timeString !== 'string') return false;

        const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
        return timeRegex.test(timeString);
    }

    /**
     * Get current time rounded to nearest 15-minute interval.
     */
    static getCurrentTimeRounded() {
        const now     = new Date();
        const hours   = now.getHours();
        const minutes = Math.round(now.getMinutes() / 15) * 15;

        if (minutes >= 60) {
            return `${(hours + 1).toString().padStart(2, '0')}:00`;
        }

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    /**
     * Check if a time is within business hours.
     */
    static isBusinessHours(timeString, startHour = 6, endHour = 22) {
        const minutes      = this.parseTimeToMinutes(timeString);
        const startMinutes = startHour * 60;
        const endMinutes   = endHour * 60;

        return minutes >= startMinutes && minutes <= endMinutes;
    }

    /**
     * Convert array of time slot strings to shift object.
     */
    static timeSlotsToShift(timeSlots, date, staffId, departmentId) {
        if (!timeSlots || timeSlots.length === 0) return null;

        const sorted = [...timeSlots].sort(
            (a, b) => this.parseTimeToMinutes(a) - this.parseTimeToMinutes(b)
        );
        const startTime       = sorted[0];
        const lastSlot        = sorted[sorted.length - 1];
        const endTime         = this.addMinutesToTime(lastSlot, 15);
        const durationMinutes = this.calculateDurationMinutes(startTime, endTime);

        return new ScheduleShift({
            staffId,
            departmentId,
            date,
            dayOfWeek:       this.getDayOfWeek(date),
            startTime,
            endTime,
            durationMinutes
        });
    }

    /**
     * Convert shift to array of 15-minute time slot strings.
     */
    static shiftToTimeSlots(shift) {
        return this.getTimeSlotsInRange(shift.startTimeString, shift.endTimeString);
    }
}

export default {
    ScheduleShift,
    WeeklySchedule,
    ScheduleTemplate,
    SchedulePeriod,
    TimeSlot,
    ScheduleUtils
};